import { useEffect } from "react";
import { API_ENDPOINTS } from "@/lib/api";

export interface VisitorRecord {
  id: string;
  ip: string;
  country: string;
  countryCode: string;
  city: string;
  region: string;
  timestamp: number;
  userAgent: string;
  path: string;
}

const STORAGE_KEY = "xinghe_visitors";
const TRACKED_KEY = "xinghe_tracked_session";

export function getVisitors(): VisitorRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as VisitorRecord[]) : [];
  } catch {
    return [];
  }
}

export async function fetchVisitors(): Promise<VisitorRecord[]> {
  try {
    const res = await fetch(API_ENDPOINTS.visitors, { headers: { Accept: "application/json" } });
    if (!res.ok) return getVisitors();
    const data = (await res.json()) as { visitors?: VisitorRecord[] };
    return Array.isArray(data.visitors) ? data.visitors : getVisitors();
  } catch {
    return getVisitors();
  }
}

function saveVisitor(record: VisitorRecord): void {
  try {
    const visitors = getVisitors();
    visitors.push(record);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visitors));
  } catch {
    // ignore storage errors
  }

  fetch(API_ENDPOINTS.visitors, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(record),
  }).catch(() => {
    // API may be unavailable during static preview.
  });
}

export function clearVisitors(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export async function clearVisitorsRemote(): Promise<void> {
  clearVisitors();
  try {
    await fetch(API_ENDPOINTS.visitorsClear, { method: "POST" });
  } catch {
    // ignore API errors in local static preview
  }
}

export function useVisitorTracker(): void {
  useEffect(() => {
    const sessionKey = sessionStorage.getItem(TRACKED_KEY);
    if (sessionKey) return;

    const fetchAndRecord = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();

        const record: VisitorRecord = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          ip: data.ip ?? "unknown",
          country: data.country_name ?? "Unknown",
          countryCode: data.country_code ?? "??",
          city: data.city ?? "Unknown",
          region: data.region ?? "Unknown",
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          path: window.location.pathname,
        };

        saveVisitor(record);
        sessionStorage.setItem(TRACKED_KEY, "1");
      } catch {
        // network fail — still record as unknown
        const record: VisitorRecord = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          ip: "unknown",
          country: "Unknown",
          countryCode: "??",
          city: "Unknown",
          region: "Unknown",
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          path: window.location.pathname,
        };
        saveVisitor(record);
        sessionStorage.setItem(TRACKED_KEY, "1");
      }
    };

    fetchAndRecord();
  }, []);
}
